import { createActionBarEffectFeature } from '../../features/selections/create-action-bar-effect-feature';
import { createAifcDebugSelectionsEffectFeature } from '../../features/selections/create-aifc-debug-selections-effect-feature';
import { createApplyFillEffectFeature } from '../../features/selections/create-apply-fill-effect-feature';
import { createCellEditingEffectFeature } from '../../features/selections/create-cell-editing-effect-feature';
import { createCheckboxCellKeyboardInputFeature } from '../../features/selections/create-checkbox-cell-keyboard-input-feature';
import { createCheckboxEffectFeature } from '../../features/selections/create-checkbox-effect-feature';
import { createClearCellsEffectFeature } from '../../features/selections/create-clear-cells-effect-feature';
import { createContextMenuEffectFeature } from '../../features/selections/create-context-menu-effect-feature';
import { createContextMenuInputFeature } from '../../features/selections/create-context-menu-input-feature';
import { createDeleteIntentHighlightEffectFeature } from '../../features/selections/create-delete-intent-highlight-effect-feature';
import { createEnrollmentExposureEffectFeature } from '../../features/selections/create-enrollment-exposure-effect-feature';
import { createFillHandleEffectFeature } from '../../features/selections/create-fill-handle-effect-feature';
import { createFillPreviewEffectFeature } from '../../features/selections/create-fill-preview-effect-feature';
import { createFocusBoxEffectFeature } from '../../features/selections/create-focus-box-effect-feature';
import { createHighlightsEffectFeature } from '../../features/selections/create-highlights-effect-feature';
import { createMouseInputFeature } from '../../features/selections/create-mouse-input-feature';
import { createPresenceEffectFeature } from '../../features/selections/create-presence-effect-feature';
import { createPublishRovoEffectFeature } from '../../features/selections/create-publish-rovo-effect-feature';
import { createSelectionsPingEffectFeature } from '../../features/selections/create-selections-ping-effect-feature';
import { createTableCellActionsKeyboardInputFeature } from '../../features/selections/create-table-cell-actions-keyboard-input-feature';
import { createTableNavigationKeyboardInputFeature } from '../../features/selections/create-table-navigation-keyboard-input-feature';
import { createTableTabNavigationKeyboardInputFeature } from '../../features/selections/create-table-tab-navigation-keyboard-input-feature';
import { createViewChangesInputFeature } from '../../features/selections/create-view-changes-input-feature';
import { createYjsChangesInputFeature } from '../../features/selections/create-yjs-changes-input-feature';
import { type SelectionFeature } from '../../features/types';
import { type SelectionFeaturesConfig, type ModeConfig } from '../types';

/**
 * The default selection features builder — shared by all modes that use
 * the standard feature set. Each mode config can override this if needed.
 */
export const buildDefaultSelectionFeatures: ModeConfig['buildSelectionFeatures'] = (
  deps,
) => {
  const modeFeatures: SelectionFeaturesConfig = {
    inputs: [
      createMouseInputFeature(deps),
      createTableNavigationKeyboardInputFeature({
        sharedLoop: deps.sharedLoop,
        adapter: deps.databaseAdapter,
      }),
      createTableTabNavigationKeyboardInputFeature({
        sharedLoop: deps.sharedLoop,
        adapter: deps.databaseAdapter,
      }),
      createCheckboxCellKeyboardInputFeature(deps),
      createTableCellActionsKeyboardInputFeature(deps),
      createContextMenuInputFeature({
        onSelectionUiInteraction: deps.onSelectionUiInteraction,
      }),
    ],
    effects: [
      /**
       * Exposure for experiment.
       */
      createEnrollmentExposureEffectFeature(),

      /**
       * Visuals
       */
      createHighlightsEffectFeature(),
      createFocusBoxEffectFeature(),
      createFillHandleEffectFeature(deps),
      createFillPreviewEffectFeature(),
      createDeleteIntentHighlightEffectFeature(),

      /**
       * UI Components
       */
      createAifcDebugSelectionsEffectFeature(),
      createActionBarEffectFeature(deps),
      createCellEditingEffectFeature(),
      createContextMenuEffectFeature(deps),
      createCheckboxEffectFeature(deps),
      createSelectionsPingEffectFeature(),

      /**
       * DB Mutations
       */
      createApplyFillEffectFeature(deps),
      createClearCellsEffectFeature(deps),
    ],
  };

  // Always-on infrastructure features
  const alwaysOnFeatures: SelectionFeature[] = [
    createViewChangesInputFeature(),
    createPresenceEffectFeature(deps),
    createPublishRovoEffectFeature(),
    createYjsChangesInputFeature(deps),
  ];

  return { modeFeatures, alwaysOnFeatures };
};
